import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import { AdminMe, api, ApiError, ClusterFeedbackSummary } from "../lib/api";
import { formatAskers } from "../lib/format";

// Organiser dashboard for a single session. URL-driven: `/admin/sessions/:id`
// fetches the session on mount, so refreshes are stable and the page is
// bookmarkable. The session list lives at `/admin` (OrgSessionList).
//
// ForwardAuth in front of the route ensures only signed-in admins reach this
// page; the backend re-checks email-allowlist server-side and tenant-filters
// every read on `user.org_id`.
type SessionRow = {
  id: string;
  join_code: string;
  // Visitor join URL pre-built by the backend from ASKTOGETHER_PUBLIC_BASE_URL.
  // Use this for QR codes / share links — never synthesise from
  // window.location.origin, which leaks the OAuth-walled admin host into the
  // URL handed to visitors.
  join_url: string;
  name: string;
  state: string;
  starts_at: string | null;
  settings: Record<string, unknown>;
};

// Cluster-side default for `similarity_threshold` — kept in lockstep with
// `backend/src/asktogether/config.py:similarity_threshold`. Used as the
// slider position when this session has no per-session override set, so
// the moderator sees what's actually active rather than starting at 0.
const DEFAULT_SIMILARITY_THRESHOLD = 0.82;

// Cluster-side default for `drafts_mode` (Phase 5). The backend treats
// any unset value as "off"; we mirror that here so the select shows the
// right option when there's no per-session override.
type DraftsMode = "off" | "generic" | "preview" | "full";
const DEFAULT_DRAFTS_MODE: DraftsMode = "off";

const DRAFTS_MODE_LABEL: Record<DraftsMode, string> = {
  off: "Off",
  generic: "Generic ('someone is typing…')",
  preview: "Truncated preview (~80 chars)",
  full: "Full text (trusted small groups)",
};

// Convert an ISO-with-offset string (or null) to the value attribute a
// <input type="datetime-local"> expects, in the user's local time. The
// browser otherwise shows an empty box even though the underlying state
// has a value.
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  // Treat the input as local time and convert to a real ISO with offset.
  return new Date(local).toISOString();
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "0s";
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

type QueueRow = {
  id: string;
  body: string;
  status: string;
  vote_count: number;
  upvote_count: number;
  askers: string[];
  anonymous_count: number;
  answered_at: string | null;
  answer_text: string | null;
  cluster_id: string | null;
  created_at: string;
};

// Distinct accent colour per cluster id. Reused across renders so the
// same group keeps the same hue on refresh — tied to the position the
// cluster id appears in the list, not the id itself, so colours stay
// stable as long as the queue ordering does.
const CLUSTER_HUES = ["#2563eb", "#9333ea", "#0ea5e9", "#db2777", "#ea580c", "#059669"];

type Tab = "setup" | "questions";

export default function OrgDashboard() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [created, setCreated] = useState<SessionRow | null>(null);
  // null while the initial fetch is in flight; set to a message string if the
  // session can't be loaded (404 most commonly — wrong id or wrong org).
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scheduleInput, setScheduleInput] = useState("");
  // Whether the "Specific time…" custom input is shown on the schedule
  // editor. Most events just want one of the next 15-min boundaries
  // (10:30, 10:45, …); the datetime-local picker lives behind a
  // disclosure toggle so the common path is one click.
  const [customScheduleOpen, setCustomScheduleOpen] = useState(false);
  const me = useOutletContext<AdminMe | null>() ?? null;
  // Moderators see only the Questions tab — they have no access to
  // settings or session lifecycle controls. Default to "questions" for
  // moderators; "setup" for everyone else.
  const isModerator = me?.role === "moderator";
  const isOperatorOrAdmin = me?.role === "operator" || me?.role === "admin";
  const [pending, setPending] = useState<QueueRow[]>([]);
  const [accepted, setAccepted] = useState<QueueRow[]>([]);
  const [rejected, setRejected] = useState<QueueRow[]>([]);
  // Pairs the moderator has marked as "not similar". Loaded alongside
  // the queue so a "Marked as different (N)" disclosure can render with
  // an undo button — without this, the marking is invisible after the
  // pair stops appearing in clusters.
  const [notSimilarPairs, setNotSimilarPairs] = useState<
    {
      feedback_id: string;
      question_a_id: string;
      question_a_body: string;
      question_b_id: string;
      question_b_body: string;
      created_at: string;
    }[]
  >([]);
  // Multi-select state for bulk "mark as similar". Spans all three
  // status columns so the moderator can pull cross-state duplicates
  // into one cluster (e.g. a pending paraphrase of an already-approved
  // question). Cleared on successful bulk-mark or manual Clear.
  const [selectedForMerge, setSelectedForMerge] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkSaving, setBulkSaving] = useState(false);
  const [tab, setTab] = useState<Tab>(isModerator ? "questions" : "setup");
  // Per-cluster expand state for the accepted column. Cluster_ids are
  // deterministic across polls when the underlying question set hasn't
  // changed, so toggling persists across the 3s refresh tick. New
  // questions arriving in a cluster will renumber and reset the entry,
  // which is fine — the default-collapsed state is the right one for
  // an already-curated approved list.
  // Locally-recorded cluster verdicts. Keyed by sorted "a:b" so the
  // value survives ordering changes when the cluster sort tweaks the
  // adjacency. The verdict either hides the pair-action row entirely
  // (after click) or marks it as "Recorded". Pre-loading from the
  // server isn't worth it — the next refresh re-clusters with
  // not_similar rows already masked, so old pairs naturally
  // disappear.
  const [pairVerdicts, setPairVerdicts] = useState<
    Record<string, "similar" | "not_similar" | "saving">
  >({});
  // Cluster-feedback summary for the operator's setup-tab banner.
  // Loaded on mount + after each verdict so the banner appears (or
  // updates) as soon as the rejection rate crosses the threshold.
  const [feedbackSummary, setFeedbackSummary] =
    useState<ClusterFeedbackSummary | null>(null);

  // Free-text + semantic search across the moderator queue. The input
  // does two things at once:
  //  * filter — only questions whose body contains the query
  //    (case-insensitive) stay visible
  //  * semantic rank — fetched from the backend with a small debounce;
  //    when present, the score becomes the primary sort key. Falls
  //    back to the default ordering when the search endpoint returns
  //    empty (embedding/qdrant failure) so the page stays usable.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScores, setSearchScores] = useState<Map<string, number> | null>(
    null,
  );
  useEffect(() => {
    if (!sessionId) return;
    const q = searchQuery.trim();
    if (!q) {
      setSearchScores(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const hits = await api.adminSessionSearch(sessionId, q);
        if (cancelled) return;
        if (hits.length === 0) {
          // Empty result == best-effort failure or genuinely no
          // matches above qdrant's own internal floor; either way,
          // don't pin everything to the bottom — fall back to no
          // semantic re-rank, leaving the substring filter as the
          // only effect.
          setSearchScores(null);
          return;
        }
        const next = new Map<string, number>();
        for (const h of hits) next.set(h.question_id, h.score);
        setSearchScores(next);
      } catch {
        if (!cancelled) setSearchScores(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery, sessionId]);

  // Cosine-similarity floor for considering a non-substring-match
  // semantically close enough to surface in the moderator's filter. 0.5
  // is roughly "obviously related theme" with nomic-embed-text; the
  // clustering threshold (0.82) is much stricter because clustering
  // implies duplicate intent, while search just implies relevance.
  const SEMANTIC_FILTER_FLOOR = 0.5;

  function applySearchFilter<T extends { id: string; body: string }>(
    rows: T[],
  ): T[] {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    const filtered = rows.filter((r) => {
      if (r.body.toLowerCase().includes(q)) return true;
      const s = searchScores?.get(r.id);
      return s != null && s >= SEMANTIC_FILTER_FLOOR;
    });
    if (searchScores) {
      // Rows with a semantic score sort first by score desc; rows that
      // only matched by substring sort below them in natural order.
      return filtered.slice().sort((a, b) => {
        const sa = searchScores.get(a.id);
        const sb = searchScores.get(b.id);
        if (sa == null && sb == null) return 0;
        if (sa == null) return 1;
        if (sb == null) return -1;
        return sb - sa;
      });
    }
    return filtered;
  }

  function pairKey(a: string, b: string) {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  }

  async function recordClusterFeedback(
    qa: string,
    qb: string,
    verdict: "similar" | "not_similar",
  ) {
    if (!sessionId) return;
    const key = pairKey(qa, qb);
    setPairVerdicts((prev) => ({ ...prev, [key]: "saving" }));
    try {
      const summary = await api.clusterFeedback(sessionId, qa, qb, verdict);
      setPairVerdicts((prev) => ({ ...prev, [key]: verdict }));
      setFeedbackSummary(summary);
      // Pull a fresh queue: not_similar verdicts re-shape clusters
      // server-side, similar verdicts just refresh counts.
      if (verdict === "not_similar" && created) {
        await refreshQuestions(created.id);
      }
    } catch (e) {
      setPairVerdicts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setErr(e instanceof ApiError ? e.message : "could not record verdict");
    }
  }

  const renderClusterPairAction = (
    cid: string,
    qa: string,
    qb: string,
    hue?: string,
  ) => {
    const key = pairKey(qa, qb);
    const verdict = pairVerdicts[key];
    if (verdict === "similar" || verdict === "not_similar") {
      return (
        <li
          key={`pair-${key}`}
          className="meta"
          style={{
            listStyle: "none",
            fontSize: "0.78rem",
            color: "var(--fg-muted)",
            margin: "0.15rem 0 0.4rem 0.6rem",
            paddingLeft: "0.4rem",
            borderLeft: hue ? `2px solid ${hue}` : undefined,
          }}
        >
          {verdict === "similar" ? "Confirmed similar." : "Marked as different topic — won't re-cluster."}
        </li>
      );
    }
    const saving = verdict === "saving";
    return (
      <li
        key={`pair-${key}`}
        data-cluster={cid}
        style={{
          listStyle: "none",
          margin: "0.15rem 0 0.5rem 0.6rem",
          paddingLeft: "0.4rem",
          borderLeft: hue ? `2px dashed ${hue}` : undefined,
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          fontSize: "0.8rem",
          color: "var(--fg-muted)",
        }}
      >
        <span>Look similar?</span>
        <button
          type="button"
          className="secondary"
          disabled={saving}
          onClick={() => recordClusterFeedback(qa, qb, "similar")}
          style={{ fontSize: "0.78rem", padding: "0.15rem 0.5rem" }}
        >
          ✓ Same
        </button>
        <button
          type="button"
          className="secondary"
          disabled={saving}
          onClick={() => recordClusterFeedback(qa, qb, "not_similar")}
          style={{
            fontSize: "0.78rem",
            padding: "0.15rem 0.5rem",
            borderColor: "var(--danger, #dc2626)",
            color: "var(--danger, #dc2626)",
          }}
        >
          ✗ Different topic
        </button>
      </li>
    );
  };
  const [err, setErr] = useState<string | null>(null);
  // Tick once per second so both the live countdown and the schedule-slot
  // wall-clock buttons stay current. The buttons need to roll forward as
  // each 15-min boundary passes (e.g. 10:28 → 10:30 disappears, 11:30
  // appears at the end). Cheap state update, only runs while the dashboard
  // is mounted and the session isn't closed.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (created?.state === "closed") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [created?.state]);

  async function refreshQuestions(sid: string) {
    // Promise.allSettled, not Promise.all: a failure in any one of these
    // four endpoints must NOT blank the others. In particular, if the
    // cluster-feedback/pairs endpoint 404s during a backend rollout (or
    // throws on a malformed row), the pending/approved queues must still
    // render — otherwise the moderator sees a blank page even though the
    // session has live questions.
    const [pRes, aRes, rRes, nsRes] = await Promise.allSettled([
      api.moderationQueue(sid, "pending"),
      api.moderationQueue(sid, "approved"),
      api.moderationQueue(sid, "rejected"),
      api.listClusterFeedbackPairs(sid, "not_similar"),
    ]);
    if (pRes.status === "fulfilled") setPending(pRes.value);
    if (aRes.status === "fulfilled") setAccepted(aRes.value);
    if (rRes.status === "fulfilled") setRejected(rRes.value);
    if (nsRes.status === "fulfilled") setNotSimilarPairs(nsRes.value);
  }

  async function unmarkNotSimilar(feedbackId: string) {
    if (!sessionId) return;
    try {
      await api.deleteClusterFeedback(sessionId, feedbackId);
      if (created) await refreshQuestions(created.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not undo marking");
    }
  }

  useEffect(() => {
    if (!created) return;
    const id = window.setInterval(() => refreshQuestions(created.id), 3000);
    refreshQuestions(created.id);
    return () => window.clearInterval(id);
  }, [created]);

  // Cluster-feedback summary: load on session change so the setup-tab
  // banner can recommend a threshold change if moderators have been
  // rejecting the auto-clusters. Best-effort; failure leaves the
  // banner unrendered.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await api.clusterFeedbackSummary(sessionId);
        if (!cancelled) setFeedbackSummary(summary);
      } catch {
        // best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Initial fetch: load the session named in the URL. Tenant scoping happens
  // server-side; a foreign session id resolves to 404. Keep `created.id` ===
  // `sessionId` so the question-refresh effect can use it without re-fetching.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const r = await api.getSession(sessionId);
        if (cancelled) return;
        setCreated({
          id: r.id,
          join_code: r.join_code,
          join_url: r.join_url,
          name: r.name,
          state: r.state,
          starts_at: r.starts_at,
          settings: r.settings ?? {},
        });
        setScheduleInput(isoToLocalInput(r.starts_at));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setLoadError("Session not found.");
        } else {
          setLoadError(
            e instanceof ApiError ? e.message : "could not load session",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  async function transition(state: "open" | "paused" | "closed") {
    if (!created) return;
    try {
      const r = await api.transitionSession(created.id, state);
      setCreated({ ...created, state: r.state });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "transition failed");
    }
  }

  async function persistSchedule(iso: string | null) {
    if (!created) return;
    setErr(null);
    try {
      const r = await api.scheduleSession(created.id, iso);
      setCreated({ ...created, state: r.state, starts_at: r.starts_at });
      setScheduleInput(isoToLocalInput(r.starts_at));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not save schedule");
    }
  }

  function saveSchedule() {
    void persistSchedule(localInputToIso(scheduleInput));
  }

  function clearSchedule() {
    setScheduleInput("");
    setCustomScheduleOpen(false);
    void persistSchedule(null);
  }

  function scheduleAt(target: Date) {
    setScheduleInput(isoToLocalInput(target.toISOString()));
    setCustomScheduleOpen(false);
    void persistSchedule(target.toISOString());
  }

  async function patchSettings(patch: {
    similarity_threshold?: number | null;
    drafts_mode?: "off" | "generic" | "preview" | "full" | null;
  }) {
    if (!created) return;
    setErr(null);
    try {
      const r = await api.patchSessionSettings(created.id, patch);
      setCreated({ ...created, settings: r.settings ?? {} });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not save settings");
    }
  }

  // Optimistic copy-to-clipboard with a brief "Copied" affordance.
  // copyTarget is a free-form key so we can show "Copied" against either
  // the join code chip or the URL chip independently.
  const [copyTarget, setCopyTarget] = useState<string | null>(null);
  async function copyText(value: string, target: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopyTarget(target);
      window.setTimeout(() => setCopyTarget((t) => (t === target ? null : t)), 1500);
    } catch {
      setErr("Copy failed — your browser may need clipboard permissions");
    }
  }

  // Next four 15-min wall-clock boundaries from `now`. Skip a boundary if
  // it's <2 min away — clicking 10:30 with 30s to go would auto-open
  // almost immediately, which is rarely the intent.
  function next15MinBoundaries(nowMs: number, count = 4): Date[] {
    const QUARTER = 15 * 60_000;
    const ceil = Math.ceil((nowMs + 2 * 60_000) / QUARTER) * QUARTER;
    return Array.from({ length: count }, (_, i) => new Date(ceil + i * QUARTER));
  }

  async function moderate(
    qid: string,
    decision: "approved" | "rejected" | "pending",
  ) {
    try {
      await api.moderate(qid, decision);
      if (created) refreshQuestions(created.id);
    } catch {
      setErr("Moderation failed");
    }
  }

  function toggleMergeSelection(qid: string) {
    setSelectedForMerge((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  }

  async function bulkMarkSimilar() {
    if (!sessionId || selectedForMerge.size < 2) return;
    setBulkSaving(true);
    try {
      const summary = await api.bulkMarkSimilar(
        sessionId,
        Array.from(selectedForMerge),
      );
      setFeedbackSummary(summary);
      setSelectedForMerge(new Set());
      if (created) await refreshQuestions(created.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not mark similar");
    } finally {
      setBulkSaving(false);
    }
  }

  // answeringId tracks which card has its inline response composer open;
  // answerDraft holds the in-progress response text. Kept at the page level
  // (not per-card) so opening another card cancels the first — only one
  // response in flight at a time, simpler than per-row state.
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answerDraft, setAnswerDraft] = useState("");

  async function submitAnswer(qid: string) {
    setErr(null);
    try {
      await api.markAnswered(qid, answerDraft.trim() || null);
      setAnsweringId(null);
      setAnswerDraft("");
      if (created) refreshQuestions(created.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not mark answered");
    }
  }

  async function unmarkAnswer(qid: string) {
    setErr(null);
    try {
      await api.unmarkAnswered(qid);
      if (created) refreshQuestions(created.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not unmark");
    }
  }

  if (loadError) {
    return (
      <main>
        <p style={{ marginBottom: "0.5rem" }}>
          <Link to="/admin">← All sessions</Link>
        </p>
        <h1>{loadError}</h1>
        <p className="meta">
          The session may have been deleted, or this id is from a different
          org.
        </p>
      </main>
    );
  }

  if (!created) {
    return (
      <main>
        <p style={{ marginBottom: "0.5rem" }}>
          <Link to="/admin">← All sessions</Link>
        </p>
        <p className="meta">Loading…</p>
      </main>
    );
  }

  // Backend builds this from ASKTOGETHER_PUBLIC_BASE_URL so the link always
  // points at the public, un-walled host (asktogether.co.uk in prod), even
  // though the organiser is reaching this page through the OAuth-walled
  // subdomain (asktogether.downloadserver.co.uk).
  const joinUrl = created.join_url;
  const stateMeta = (() => {
    switch (created.state) {
      case "setup":
        return { color: "#6b7280", label: "Not started", banner: "Attendees can join with the code but cannot submit questions yet. Click Open to start." };
      case "open":
        return { color: "#16a34a", label: "Open", banner: null as string | null };
      case "paused":
        return { color: "#d97706", label: "Paused", banner: "New questions are paused. Existing questions stay visible." };
      case "closed":
        return { color: "#dc2626", label: "Closed", banner: "This event is closed. Attendees see the board read-only." };
      default:
        return { color: "#6b7280", label: created.state, banner: null as string | null };
    }
  })();

  const renderQuestionCard = (
    q: QueueRow,
    actions: { approve: boolean; reject: boolean; answer: boolean },
    clusterColor?: string,
  ) => {
    const askersLine = formatAskers(q.askers, q.anonymous_count);
    const isAnswered = !!q.answered_at;
    const isComposing = answeringId === q.id;
    const accent = isAnswered ? "#16a34a" : clusterColor;
    const isSelected = selectedForMerge.has(q.id);
    return (
      <li
        key={q.id}
        className="card"
        style={{
          position: "relative",
          ...(accent
            ? {
                opacity: isAnswered ? 0.85 : 1,
                borderLeft: `3px solid ${accent}`,
              }
            : null),
          ...(isSelected
            ? { outline: "2px solid #2563eb", outlineOffset: "-2px" }
            : null),
        }}
      >
        <label
          style={{
            position: "absolute",
            top: "0.5rem",
            left: "0.5rem",
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
            padding: "0.15rem",
          }}
          title="Select to bulk-mark as similar"
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleMergeSelection(q.id)}
            aria-label={`Select question for bulk-merge: ${q.body.slice(0, 60)}`}
            style={{ cursor: "pointer", margin: 0 }}
          />
        </label>
        <span
          aria-label={`${q.upvote_count} upvotes`}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
            padding: "0.25rem 0.6rem",
            fontSize: "0.85rem",
            fontWeight: 600,
            borderRadius: "999px",
            background: "var(--surface-elevated)",
            color: "var(--fg)",
            border: "1px solid var(--border)",
          }}
        >
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {q.upvote_count}
          </span>
          <span aria-hidden="true">👍</span>
        </span>
        <div
          className="body"
          style={{
            paddingLeft: "1.75rem",
            paddingRight: "4rem",
            ...(isAnswered ? { textDecoration: "line-through" } : null),
          }}
        >
          {q.body}
        </div>
        {askersLine && (
          <div className="meta" style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
            Asked by {askersLine}
          </div>
        )}
        <div className="meta" style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
          {(() => {
            const voicesCount = q.askers.length + q.anonymous_count;
            return `${voicesCount} ${voicesCount === 1 ? "voice" : "voices"}`;
          })()}
          {isAnswered && (
            <span style={{ marginLeft: "0.5rem", color: "#16a34a", fontWeight: 600 }}>
              · Answered
            </span>
          )}
        </div>
        {isAnswered && q.answer_text && (
          <div
            className="meta"
            style={{
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(22,163,74,0.08)",
              borderRadius: "4px",
              fontSize: "0.9rem",
            }}
          >
            <strong>Response:</strong> {q.answer_text}
          </div>
        )}
        {isComposing && (
          <div style={{ marginTop: "0.5rem" }}>
            <textarea
              value={answerDraft}
              onChange={(e) => setAnswerDraft(e.target.value)}
              placeholder="Optional response (leave blank to just mark answered)…"
              maxLength={4096}
              rows={2}
              style={{ width: "100%" }}
            />
            <div className="row" style={{ gap: "0.5rem", marginTop: "0.25rem" }}>
              <button onClick={() => submitAnswer(q.id)}>Mark answered</button>
              <button
                className="secondary"
                onClick={() => {
                  setAnsweringId(null);
                  setAnswerDraft("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {!isComposing && (actions.approve || actions.reject || actions.answer) && (
          <div className="row" style={{ marginTop: "0.5rem", gap: "0.5rem", flexWrap: "wrap" }}>
            {actions.approve && (
              <button onClick={() => moderate(q.id, "approved")}>Approve</button>
            )}
            {actions.answer && !isAnswered && (
              <button
                onClick={() => {
                  setAnsweringId(q.id);
                  setAnswerDraft(q.answer_text ?? "");
                }}
              >
                Mark answered
              </button>
            )}
            {actions.answer && isAnswered && (
              <>
                <button
                  className="secondary"
                  onClick={() => {
                    setAnsweringId(q.id);
                    setAnswerDraft(q.answer_text ?? "");
                  }}
                >
                  Edit response
                </button>
                <button className="secondary" onClick={() => unmarkAnswer(q.id)}>
                  Unmark
                </button>
              </>
            )}
            {actions.reject && (
              <button className="secondary" onClick={() => moderate(q.id, "rejected")}>
                Reject
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <main>
      <p style={{ marginBottom: "0.25rem" }}>
        <Link to="/admin">← All sessions</Link>
      </p>
      <h1>{created.name}</h1>
      <p
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.75rem",
          borderRadius: "999px",
          background: stateMeta.color,
          color: "white",
          fontWeight: 600,
          fontSize: "0.85rem",
        }}
        aria-label={`Session state: ${stateMeta.label}`}
      >
        ● {stateMeta.label}
      </p>

      {stateMeta.banner && (
        <div
          role="status"
          className="card meta"
          style={{ marginTop: "0.5rem", marginBottom: "1rem" }}
        >
          {stateMeta.banner}
        </div>
      )}

      <div role="tablist" aria-label="Dashboard sections" className="tabs">
        {!isModerator && (
          <button
            role="tab"
            aria-selected={tab === "setup"}
            onClick={() => setTab("setup")}
          >
            Setup &amp; controls
          </button>
        )}
        <button
          role="tab"
          aria-selected={tab === "questions"}
          onClick={() => setTab("questions")}
        >
          Questions ({pending.length} pending · {accepted.length} accepted)
        </button>
        {isOperatorOrAdmin && (
          <Link
            to={`/admin/sessions/${created?.id ?? ""}/moderators`}
            style={{
              padding: "0.35rem 0.75rem",
              textDecoration: "none",
              color: "var(--fg-muted)",
              fontSize: "0.9rem",
              alignSelf: "center",
            }}
          >
            Moderators →
          </Link>
        )}
      </div>

      {err && <p className="error" role="alert">{err}</p>}

      {tab === "setup" && !isModerator ? (
        <section role="tabpanel" aria-label="Setup and controls">
          {feedbackSummary?.suggested_threshold != null && (
            <div
              role="status"
              style={{
                margin: "0 0 1rem",
                padding: "0.75rem 1rem",
                border: "1px solid #d97706",
                background: "rgba(217, 119, 6, 0.08)",
                borderRadius: "8px",
                display: "flex",
                gap: "0.75rem",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: "240px", lineHeight: 1.4 }}>
                <strong>Similarity threshold may be too loose.</strong>{" "}
                Moderators marked{" "}
                <strong>{feedbackSummary.rejected_count}</strong> cluster
                suggestion{feedbackSummary.rejected_count === 1 ? "" : "s"}{" "}
                as different topics on this session
                {feedbackSummary.confirmed_count > 0 && (
                  <>
                    {" "}
                    (vs {feedbackSummary.confirmed_count} confirmed)
                  </>
                )}
                . Current threshold is{" "}
                <code>{feedbackSummary.current_threshold.toFixed(2)}</code>.
                Try raising it to{" "}
                <code>
                  {feedbackSummary.suggested_threshold.toFixed(2)}
                </code>{" "}
                — looser matches will be discarded and the moderator queue
                should cluster more accurately.
              </div>
              <button
                type="button"
                onClick={() =>
                  feedbackSummary.suggested_threshold != null
                  && patchSettings({
                    similarity_threshold:
                      feedbackSummary.suggested_threshold,
                  })
                }
              >
                Raise to{" "}
                {feedbackSummary.suggested_threshold.toFixed(2)}
              </button>
            </div>
          )}
          <h2>Join code</h2>
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "0.75rem",
                borderRadius: "8px",
                lineHeight: 0,
              }}
              aria-label="QR code linking to the attendee join page"
            >
              <QRCodeSVG value={joinUrl} size={160} level="M" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <span className="code">{created.join_code}</span>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => copyText(created.join_code, "code")}
                  title="Copy join code"
                  aria-label="Copy join code"
                  style={{ padding: "0.25rem 0.6rem" }}
                >
                  {copyTarget === "code" ? "Copied" : "Copy"}
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  color: "var(--fg-muted)",
                }}
              >
                <span style={{ fontSize: "0.9rem" }}>Or share:</span>
                {/* Selecting the link selects just the URL — right-click →
                    Copy works as a native fallback. The button is the
                    fast path. */}
                <a
                  href={joinUrl}
                  onClick={(e) => e.preventDefault()}
                  style={{ wordBreak: "break-all" }}
                >
                  <code>{joinUrl}</code>
                </a>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => copyText(joinUrl, "url")}
                  title="Copy join URL"
                  aria-label="Copy join URL"
                  style={{ padding: "0.25rem 0.6rem" }}
                >
                  {copyTarget === "url" ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>

          <h2>Lifecycle</h2>
          <div className="row">
            <button
              onClick={() => transition("open")}
              disabled={created.state === "open"}
              className={created.state === "open" ? "secondary" : undefined}
            >
              {created.state === "setup"
                ? "Open event"
                : created.state === "paused"
                ? "Resume"
                : created.state === "closed"
                ? "Reopen"
                : "Open"}
            </button>
            <button
              className="secondary"
              onClick={() => transition("paused")}
              disabled={created.state !== "open"}
              title={
                created.state !== "open"
                  ? "Only an open event can be paused"
                  : undefined
              }
            >
              Pause
            </button>
            <button
              className="secondary"
              onClick={() => transition("closed")}
              disabled={created.state === "closed"}
            >
              Close
            </button>
          </div>

          <h2>Schedule</h2>
          {created.starts_at && created.state === "setup" ? (
            <p
              aria-live="polite"
              style={{
                margin: 0,
                padding: "0.75rem 1rem",
                background: "rgba(22,163,74,0.08)",
                borderLeft: "3px solid #16a34a",
                borderRadius: "4px",
              }}
            >
              Auto-opens at <strong>{new Date(created.starts_at).toLocaleTimeString()}</strong>
              {(() => {
                const remaining = new Date(created.starts_at).getTime() - now;
                return remaining > 0
                  ? <> · in {formatCountdown(remaining)}</>
                  : <> · due now, opening on next request</>;
              })()}
            </p>
          ) : (
            <p style={{ margin: 0, color: "var(--fg-muted)", fontSize: "0.9rem" }}>
              {created.starts_at
                ? `Was scheduled for ${new Date(created.starts_at).toLocaleString()}.`
                : "Not scheduled — opens manually."}
            </p>
          )}
          {created.state !== "closed" && (
            <>
              <div
                className="row"
                style={{ marginTop: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}
              >
                {next15MinBoundaries(now).map((slot) => (
                  <button
                    key={slot.getTime()}
                    className="secondary"
                    onClick={() => scheduleAt(slot)}
                    title={slot.toLocaleString()}
                  >
                    {slot.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </button>
                ))}
                <button
                  className="secondary"
                  onClick={() => setCustomScheduleOpen((v) => !v)}
                  aria-expanded={customScheduleOpen}
                >
                  {customScheduleOpen ? "Cancel custom" : "Specific time…"}
                </button>
                {created.starts_at && (
                  <button type="button" className="secondary" onClick={clearSchedule}>
                    Clear
                  </button>
                )}
              </div>
              {customScheduleOpen && (
                <div
                  className="row"
                  style={{ marginTop: "0.5rem", alignItems: "center", gap: "0.5rem" }}
                >
                  <input
                    type="datetime-local"
                    value={scheduleInput}
                    onChange={(e) => setScheduleInput(e.target.value)}
                    aria-label="Scheduled start time"
                  />
                  <button onClick={saveSchedule} disabled={!scheduleInput}>
                    Save
                  </button>
                </div>
              )}
            </>
          )}

          <h2>Similarity threshold</h2>
          {(() => {
            const overrideRaw = (created.settings as { similarity_threshold?: unknown })
              .similarity_threshold;
            const hasOverride = typeof overrideRaw === "number";
            const value = hasOverride
              ? (overrideRaw as number)
              : DEFAULT_SIMILARITY_THRESHOLD;
            return (
              <>
                <p style={{ margin: "0 0 0.5rem", color: "var(--fg-muted)", fontSize: "0.9rem" }}>
                  How similar a draft must be to a previous question before
                  it's flagged as a duplicate. Higher = stricter (fewer
                  matches), lower = looser (more grouping).
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    type="range"
                    min={0.5}
                    max={0.95}
                    step={0.01}
                    value={value}
                    aria-label="Similarity threshold"
                    onChange={(e) =>
                      // Optimistic update so the slider feels live; the
                      // PATCH lands on release (onMouseUp/onTouchEnd).
                      setCreated({
                        ...created,
                        settings: {
                          ...created.settings,
                          similarity_threshold: Number(e.target.value),
                        },
                      })
                    }
                    onMouseUp={(e) =>
                      void patchSettings({
                        similarity_threshold: Number(
                          (e.target as HTMLInputElement).value,
                        ),
                      })
                    }
                    onTouchEnd={(e) =>
                      void patchSettings({
                        similarity_threshold: Number(
                          (e.target as HTMLInputElement).value,
                        ),
                      })
                    }
                    style={{ flex: 1, minWidth: "200px" }}
                  />
                  <span className="code" style={{ minWidth: "3.5rem", textAlign: "right" }}>
                    {value.toFixed(2)}
                  </span>
                  {hasOverride ? (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        void patchSettings({ similarity_threshold: null })
                      }
                      title={`Reset to cluster default (${DEFAULT_SIMILARITY_THRESHOLD.toFixed(2)})`}
                    >
                      Reset to default
                    </button>
                  ) : (
                    <span style={{ color: "var(--fg-muted)", fontSize: "0.9rem" }}>
                      (default)
                    </span>
                  )}
                </div>
              </>
            );
          })()}

          <h2 style={{ marginTop: "1.5rem" }}>Live typing similarity</h2>
          {(() => {
            const overrideRaw = (created.settings as { drafts_mode?: unknown })
              .drafts_mode;
            const hasOverride =
              overrideRaw === "off"
              || overrideRaw === "generic"
              || overrideRaw === "preview"
              || overrideRaw === "full";
            const value = hasOverride
              ? (overrideRaw as "off" | "generic" | "preview" | "full")
              : DEFAULT_DRAFTS_MODE;
            return (
              <>
                <p
                  style={{
                    margin: "0 0 0.5rem",
                    color: "var(--fg-muted)",
                    fontSize: "0.9rem",
                  }}
                >
                  Surface other attendees' in-flight drafts to typers, before
                  anyone submits. Higher modes leak more text — pick the level
                  appropriate for your audience.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <select
                    aria-label="Live typing similarity mode"
                    value={value}
                    onChange={(e) =>
                      void patchSettings({
                        drafts_mode: e.target.value as
                          | "off"
                          | "generic"
                          | "preview"
                          | "full",
                      })
                    }
                    style={{ flex: 1, minWidth: "240px" }}
                  >
                    {(["off", "generic", "preview", "full"] as const).map(
                      (m) => (
                        <option key={m} value={m}>
                          {DRAFTS_MODE_LABEL[m]}
                        </option>
                      ),
                    )}
                  </select>
                  {hasOverride ? (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => void patchSettings({ drafts_mode: null })}
                      title={`Reset to cluster default (${DRAFTS_MODE_LABEL[DEFAULT_DRAFTS_MODE]})`}
                    >
                      Reset to default
                    </button>
                  ) : (
                    <span
                      style={{ color: "var(--fg-muted)", fontSize: "0.9rem" }}
                    >
                      (default)
                    </span>
                  )}
                </div>
                <ul
                  style={{
                    margin: "0.75rem 0 0",
                    paddingLeft: "1.2rem",
                    color: "var(--fg-muted)",
                    fontSize: "0.85rem",
                  }}
                >
                  <li>
                    <strong>Off</strong> — drafts aren't streamed. Lowest
                    privacy cost, no live similarity.
                  </li>
                  <li>
                    <strong>Generic</strong> — typers see "someone is typing a
                    similar question" with no text. Safest middle ground.
                  </li>
                  <li>
                    <strong>Truncated preview</strong> — first ~80 chars +
                    ellipsis. Useful when topic is fine to share but verbatim
                    quoting is not.
                  </li>
                  <li>
                    <strong>Full text</strong> — full draft visible. Use only
                    with trusted small groups.
                  </li>
                </ul>
              </>
            );
          })()}
        </section>
      ) : (
        <section role="tabpanel" aria-label="Questions">
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "1rem",
            }}
          >
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter & sort questions by meaning…"
              aria-label="Filter and semantically sort questions"
              style={{ flex: 1, minWidth: "200px" }}
            />
            {searchQuery.trim() && (
              <button
                type="button"
                className="secondary"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                style={{ padding: "0.35rem 0.7rem" }}
              >
                Clear
              </button>
            )}
            {searchQuery.trim() && (
              <span
                className="meta"
                style={{ fontSize: "0.78rem", color: "var(--fg-muted)" }}
                aria-live="polite"
              >
                {searchScores
                  ? "Substring match + relevance sort"
                  : "Substring match"}
              </span>
            )}
          </div>
          {(() => {
            // Split accepted into unanswered + answered so the answered
            // section can render below the accepted list. Both sub-lists
            // are then sorted by upvote_count desc — the moderator wants
            // the strongest crowd-signal at the top — with vote_count as
            // a tiebreaker (combined voices+upvotes catches strong
            // "Add my name" momentum on questions with low pure-upvote
            // counts) and created_at as a final stable sort key.
            const sortByUpvotes = <T extends {
              upvote_count: number;
              vote_count: number;
              created_at: string;
            }>(rows: T[]): T[] =>
              [...rows].sort((a, b) => {
                if (b.upvote_count !== a.upvote_count) {
                  return b.upvote_count - a.upvote_count;
                }
                if (b.vote_count !== a.vote_count) {
                  return b.vote_count - a.vote_count;
                }
                return a.created_at.localeCompare(b.created_at);
              });

            const unanswered = applySearchFilter(
              sortByUpvotes(accepted.filter((q) => !q.answered_at)),
            );
            const answered = applySearchFilter(
              sortByUpvotes(accepted.filter((q) => !!q.answered_at)),
            );
            const pendingVisible = applySearchFilter(pending);

            // Build cluster-aware groups for the accepted (unanswered)
            // list. Each group is either a singleton (one question) or a
            // cluster of similar questions whose lead is the strongest
            // crowd-signal member; the rest are kept under a "+N similar"
            // disclosure so the column reads cleanly when many similar
            // questions have been let through. Groups themselves are
            // sorted by lead's upvote/vote/created — so a hot singleton
            // can outrank a small lukewarm cluster.
            type ClusterGroup = {
              cluster_id: string | null;
              lead: QueueRow;
              rest: QueueRow[];
              hue?: string;
            };
            const acceptedHueFor: Record<string, string> = {};
            let acceptedHueNext = 0;
            const byCluster: Record<string, QueueRow[]> = {};
            const acceptedSingletons: QueueRow[] = [];
            for (const q of unanswered) {
              if (q.cluster_id) {
                if (!byCluster[q.cluster_id]) byCluster[q.cluster_id] = [];
                byCluster[q.cluster_id].push(q);
              } else {
                acceptedSingletons.push(q);
              }
            }
            const acceptedGroups: ClusterGroup[] = [];
            for (const [cid, members] of Object.entries(byCluster)) {
              const sorted = sortByUpvotes(members);
              acceptedHueFor[cid] =
                CLUSTER_HUES[acceptedHueNext % CLUSTER_HUES.length];
              acceptedHueNext++;
              acceptedGroups.push({
                cluster_id: cid,
                lead: sorted[0],
                rest: sorted.slice(1),
                hue: acceptedHueFor[cid],
              });
            }
            for (const s of acceptedSingletons) {
              acceptedGroups.push({
                cluster_id: null,
                lead: s,
                rest: [],
              });
            }
            acceptedGroups.sort((a, b) => {
              if (b.lead.upvote_count !== a.lead.upvote_count) {
                return b.lead.upvote_count - a.lead.upvote_count;
              }
              if (b.lead.vote_count !== a.lead.vote_count) {
                return b.lead.vote_count - a.lead.vote_count;
              }
              return a.lead.created_at.localeCompare(b.lead.created_at);
            });

            const renderAcceptedGroup = (g: ClusterGroup): JSX.Element[] => {
              const out: JSX.Element[] = [];
              if (g.cluster_id === null || g.rest.length === 0) {
                out.push(
                  renderQuestionCard(
                    g.lead,
                    { approve: false, reject: true, answer: true },
                    g.hue,
                  ),
                );
                return out;
              }
              // No more collapsed "+N similar" disclosure — show all
              // cluster members visibly with the colour rule, and
              // interleave a "look similar?" affordance between each
              // adjacent pair so the moderator can confirm or reject
              // the suggestion. Rejected pairs are immediately hidden
              // from the suggestion row and excluded from clustering
              // on the next refresh (see backend ClusterFeedback).
              const members = [g.lead, ...g.rest];
              for (let i = 0; i < members.length; i++) {
                out.push(
                  renderQuestionCard(
                    members[i],
                    { approve: false, reject: true, answer: true },
                    g.hue,
                  ),
                );
                if (i < members.length - 1) {
                  out.push(
                    renderClusterPairAction(
                      g.cluster_id ?? "",
                      members[i].id,
                      members[i + 1].id,
                      g.hue,
                    ),
                  );
                }
              }
              return out;
            };

            // When there's nothing pending, collapse the two-column grid
            // and let the right-hand content (accepted + answered) take
            // the whole width. Saves the moderator from staring at an
            // empty "Queue is empty" placeholder during the long stretches
            // of an open event after the last clearance.
            const hasPending = pendingVisible.length > 0;

            // The right-column (or full-width when no pending) content:
            // accepted on top, answered below as its own section.
            const rightColumn = (
              <>
                <h2>Accepted ({unanswered.length})</h2>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {acceptedGroups.flatMap(renderAcceptedGroup)}
                  {unanswered.length === 0 && (
                    <li className="meta">No questions accepted yet.</li>
                  )}
                </ul>
                <h2 style={{ marginTop: "1.5rem" }}>
                  Answered ({answered.length})
                </h2>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {answered.length === 0 ? (
                    <li className="meta">
                      Questions you mark as answered appear here.
                    </li>
                  ) : (
                    answered.map((q) =>
                      renderQuestionCard(q, {
                        approve: false,
                        reject: true,
                        answer: true,
                      }),
                    )
                  )}
                </ul>
              </>
            );

            if (!hasPending) {
              return <div>{rightColumn}</div>;
            }

            // Pending column with cluster grouping (unchanged from prior).
            const hueFor: Record<string, string> = {};
            let nextHue = 0;
            for (const q of pendingVisible) {
              if (q.cluster_id && !(q.cluster_id in hueFor)) {
                hueFor[q.cluster_id] =
                  CLUSTER_HUES[nextHue % CLUSTER_HUES.length];
                nextHue++;
              }
            }
            // Per-cluster size for the header label so the moderator
            // immediately sees how many questions are in each group.
            // Backend sorts clusters by size desc, then by upvotes within,
            // so the first card after each header is the cluster's lead.
            const pendingClusterSize: Record<string, number> = {};
            for (const q of pendingVisible) {
              if (q.cluster_id) {
                pendingClusterSize[q.cluster_id] =
                  (pendingClusterSize[q.cluster_id] ?? 0) + 1;
              }
            }
            const pendingElements: JSX.Element[] = [];
            let prevCluster: string | null = null;
            let prevQ: QueueRow | null = null;
            for (const q of pendingVisible) {
              if (q.cluster_id && q.cluster_id !== prevCluster) {
                const size = pendingClusterSize[q.cluster_id] ?? 0;
                pendingElements.push(
                  <li
                    key={`hdr-${q.cluster_id}`}
                    className="meta"
                    style={{
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: hueFor[q.cluster_id],
                      margin: "0.75rem 0 0.25rem",
                      fontWeight: 600,
                    }}
                    aria-label={`Similar question group of ${size}`}
                  >
                    ◆ These look similar — confirm or split as you triage
                  </li>,
                );
              } else if (
                q.cluster_id
                && q.cluster_id === prevCluster
                && prevQ
              ) {
                // Adjacent cluster-mates — interleave the verdict row.
                pendingElements.push(
                  renderClusterPairAction(
                    q.cluster_id,
                    prevQ.id,
                    q.id,
                    hueFor[q.cluster_id],
                  ),
                );
              }
              pendingElements.push(
                renderQuestionCard(
                  q,
                  { approve: true, reject: true, answer: false },
                  q.cluster_id ? hueFor[q.cluster_id] : undefined,
                ),
              );
              prevCluster = q.cluster_id;
              prevQ = q;
            }

            return (
              <>
                {selectedForMerge.size > 0 && (
                  <div
                    style={{
                      position: "sticky",
                      top: "0.5rem",
                      zIndex: 5,
                      marginBottom: "0.75rem",
                      padding: "0.5rem 0.75rem",
                      background: "#2563eb",
                      color: "white",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                    role="toolbar"
                    aria-label="Bulk selection actions"
                  >
                    <span style={{ fontWeight: 600 }}>
                      {selectedForMerge.size} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => void bulkMarkSimilar()}
                      disabled={bulkSaving || selectedForMerge.size < 2}
                      style={{
                        background: "white",
                        color: "#2563eb",
                        border: "none",
                        padding: "0.35rem 0.75rem",
                        borderRadius: "4px",
                        fontWeight: 600,
                        cursor:
                          selectedForMerge.size < 2 || bulkSaving
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          selectedForMerge.size < 2 || bulkSaving ? 0.6 : 1,
                      }}
                    >
                      {bulkSaving
                        ? "Marking…"
                        : selectedForMerge.size < 2
                          ? "Select 2+ to mark similar"
                          : "Mark as similar"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedForMerge(new Set())}
                      disabled={bulkSaving}
                      style={{
                        background: "transparent",
                        color: "white",
                        border: "1px solid rgba(255,255,255,0.5)",
                        padding: "0.35rem 0.75rem",
                        borderRadius: "4px",
                        cursor: bulkSaving ? "not-allowed" : "pointer",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <h2>Pending ({pending.length})</h2>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {pendingElements}
                    </ul>
                  </div>
                  <div>{rightColumn}</div>
                </div>
                {notSimilarPairs.length > 0 && (
                  <details
                    style={{
                      marginTop: "2rem",
                      borderTop: "1px solid var(--border)",
                      paddingTop: "1rem",
                    }}
                  >
                    <summary
                      style={{
                        cursor: "pointer",
                        color: "var(--fg-muted)",
                        fontSize: "0.9rem",
                        userSelect: "none",
                      }}
                    >
                      Marked as different ({notSimilarPairs.length}) — click to expand
                    </summary>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "0.75rem",
                      }}
                    >
                      {notSimilarPairs.map((p) => (
                        <li
                          key={p.feedback_id}
                          style={{
                            border: "1px solid var(--border)",
                            borderRadius: "6px",
                            padding: "0.75rem",
                            marginBottom: "0.5rem",
                            background: "var(--bg-muted)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.85rem",
                              color: "var(--fg-muted)",
                              marginBottom: "0.25rem",
                            }}
                          >
                            Won't re-cluster:
                          </div>
                          <div style={{ marginBottom: "0.25rem" }}>
                            <strong>A:</strong> {p.question_a_body}
                          </div>
                          <div style={{ marginBottom: "0.5rem" }}>
                            <strong>B:</strong> {p.question_b_body}
                          </div>
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => unmarkNotSimilar(p.feedback_id)}
                            aria-label="Undo 'marked as different' for this pair"
                          >
                            ↶ Undo (allow re-cluster)
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {rejected.length > 0 && (
                  <details
                    style={{
                      marginTop: "1rem",
                      borderTop: "1px solid var(--border)",
                      paddingTop: "1rem",
                    }}
                  >
                    <summary
                      style={{
                        cursor: "pointer",
                        color: "var(--fg-muted)",
                        fontSize: "0.9rem",
                        userSelect: "none",
                      }}
                    >
                      Rejected ({rejected.length}) — click to expand
                    </summary>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "0.75rem",
                      }}
                    >
                      {applySearchFilter(rejected).map((q) => {
                        const isSelected = selectedForMerge.has(q.id);
                        return (
                          <li
                            key={q.id}
                            style={{
                              border: "1px solid var(--border)",
                              borderRadius: "6px",
                              padding: "0.75rem",
                              marginBottom: "0.5rem",
                              background: "var(--bg-muted)",
                              opacity: 0.85,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "0.5rem",
                              ...(isSelected
                                ? { outline: "2px solid #2563eb" }
                                : null),
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleMergeSelection(q.id)}
                              aria-label={`Select rejected question for bulk-merge: ${q.body.slice(0, 60)}`}
                              style={{ marginTop: "0.25rem", cursor: "pointer" }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  textDecoration: "line-through",
                                  color: "var(--fg-muted)",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                {q.body}
                              </div>
                              <button
                                type="button"
                                className="secondary"
                                onClick={() => moderate(q.id, "pending")}
                                aria-label="Reverse reject and return to pending"
                              >
                                ↶ Reverse reject
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
              </>
            );
          })()}
        </section>
      )}
    </main>
  );
}
